/**
 * Standard MIDI File reading and writing.
 *
 * `.mid` is how MIDI moves between applications, so this is the seam where a
 * part written here can go to someone else's DAW and come back. It is a pure
 * byte-level module — no project types, no DOM — which is what lets every awkward
 * corner of the format be tested directly: variable-length quantities, running
 * status, format 0 files that put sixteen instruments in one track, and files
 * whose tick resolution is nothing like ours.
 *
 * What is read: note on/off, tempo, time signature, track name, and channel
 * numbers. What is deliberately skipped: controllers, pitch bend, program
 * change, sysex and every other meta event — the DAW has nowhere to put them
 * yet, and silently dropping them is honest as long as the documentation says
 * so, whereas guessing would not be.
 */

export interface MidiFileNote {
  pitch: number;
  velocity: number;
  startTicks: number;
  durationTicks: number;
  channel: number;
}

/** A continuous controller: `controller` is the CC number, `value` is 0-127. */
export interface MidiControllerEvent {
  tick: number;
  channel: number;
  controller: number;
  value: number;
}

/**
 * A pitch bend, as a fraction of the bend range rather than as raw bytes.
 *
 * The wire format is fourteen bits with 8192 at rest, which is an arbitrary
 * number to hand to anything downstream. -1 to 1 says what it means; how many
 * semitones that is depends on a bend-range RPN this reader does not follow.
 */
export interface MidiPitchBendEvent {
  tick: number;
  channel: number;
  value: number;
}

export interface MidiProgramChangeEvent {
  tick: number;
  channel: number;
  program: number;
}

export interface MidiFileTrack {
  name: string | null;
  /** The channel the track's notes were on, when they all shared one. */
  channel: number | null;
  notes: MidiFileNote[];
  /**
   * The performance beyond the notes.
   *
   * Kept apart from the notes because they answer different questions — a note
   * is a thing that happens, a controller is a thing that is true until it
   * changes — and because what an importer can do with each is different. See
   * docs/MIDI_FILES.md for which of these find a home and which are only
   * reported.
   */
  controllers: MidiControllerEvent[];
  pitchBends: MidiPitchBendEvent[];
  programChanges: MidiProgramChangeEvent[];
}

export interface MidiTempoEvent {
  tick: number;
  bpm: number;
}

export interface MidiTimeSignatureEvent {
  tick: number;
  numerator: number;
  denominator: number;
}

export interface ParsedMidiFile {
  format: number;
  /** Ticks per quarter note, as the file itself expresses time. */
  ppq: number;
  tempoEvents: MidiTempoEvent[];
  timeSignatures: MidiTimeSignatureEvent[];
  tracks: MidiFileTrack[];
  warnings: string[];
}

export class MidiFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MidiFileError';
  }
}

const MAX_NOTES = 200_000;
/**
 * A ceiling on each kind of performance event, per track.
 *
 * A controller ride recorded from a wheel is one event every few milliseconds,
 * so a long file can carry tens of thousands of them per track legitimately.
 * This is a guard against a hostile or corrupt file rather than a musical
 * limit, and what is dropped is reported like everything else.
 */
const MAX_EVENTS = 100_000;

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

class Reader {
  offset = 0;
  constructor(readonly bytes: Uint8Array) {}

  get remaining(): number {
    return this.bytes.length - this.offset;
  }

  u8(): number {
    if (this.offset >= this.bytes.length) throw new MidiFileError('The MIDI file ends in the middle of an event.');
    return this.bytes[this.offset++];
  }

  u16(): number {
    return (this.u8() << 8) | this.u8();
  }

  u32(): number {
    return ((this.u8() << 24) | (this.u8() << 16) | (this.u8() << 8) | this.u8()) >>> 0;
  }

  ascii(length: number): string {
    let out = '';
    for (let i = 0; i < length; i++) out += String.fromCharCode(this.u8());
    return out;
  }

  /**
   * A variable-length quantity: seven bits per byte, high bit set on all but
   * the last. This is how every delta time in the format is stored.
   */
  varint(): number {
    let value = 0;
    for (let i = 0; i < 4; i++) {
      const byte = this.u8();
      value = (value << 7) | (byte & 0x7f);
      if ((byte & 0x80) === 0) return value;
    }
    throw new MidiFileError('The MIDI file contains a malformed length value.');
  }

  skip(length: number): void {
    if (length < 0 || this.offset + length > this.bytes.length) {
      throw new MidiFileError('The MIDI file claims an event is longer than the file.');
    }
    this.offset += length;
  }
}

function decodeText(bytes: Uint8Array): string {
  try {
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    // Track names come from arbitrary files, so control characters are stripped
    // by an explicit scan rather than a regex — the source file then contains
    // no control-character literals of its own.
    let out = '';
    for (const ch of decoded) {
      const code = ch.codePointAt(0) ?? 0;
      if (code >= 32 && code !== 127) out += ch;
    }
    return out.trim();
  } catch {
    return '';
  }
}

export function decodeMidiFile(source: Uint8Array | ArrayBuffer): ParsedMidiFile {
  const bytes = source instanceof Uint8Array ? source : new Uint8Array(source);
  const reader = new Reader(bytes);
  const warnings: string[] = [];

  if (bytes.length < 14 || reader.ascii(4) !== 'MThd') {
    throw new MidiFileError('This file is not a Standard MIDI File.');
  }

  const headerLength = reader.u32();
  const format = reader.u16();
  const trackCount = reader.u16();
  const division = reader.u16();
  // Headers are 6 bytes today, but the format allows longer ones.
  if (headerLength > 6) reader.skip(headerLength - 6);

  if ((division & 0x8000) !== 0) {
    throw new MidiFileError(
      'This MIDI file uses SMPTE timecode rather than musical time, which Stage Zero cannot import. ' +
        'Re-export it from its source with a tempo-based resolution.',
    );
  }
  const ppq = division;
  if (ppq <= 0) throw new MidiFileError('This MIDI file declares a tick resolution of zero.');

  if (format > 1) {
    warnings.push(
      `This is a format ${format} MIDI file, whose tracks are independent sequences. ` +
        'They have been imported as parallel parts starting together.',
    );
  }

  const tempoEvents: MidiTempoEvent[] = [];
  const timeSignatures: MidiTimeSignatureEvent[] = [];
  const tracks: MidiFileTrack[] = [];
  let totalNotes = 0;

  for (let index = 0; index < trackCount; index++) {
    if (reader.remaining < 8) {
      warnings.push(`The file declares ${trackCount} tracks but contains ${index}.`);
      break;
    }
    const id = reader.ascii(4);
    const length = reader.u32();
    if (id !== 'MTrk') {
      // Unknown chunk types are skipped by design; the format requires it.
      reader.skip(length);
      continue;
    }

    const end = reader.offset + length;
    const track = readTrack(reader, end, tempoEvents, timeSignatures, warnings, () => totalNotes >= MAX_NOTES);
    totalNotes += track.notes.length;
    reader.offset = Math.min(end, bytes.length);
    tracks.push(track);
  }

  if (totalNotes >= MAX_NOTES) {
    warnings.push(`This file contains more than ${MAX_NOTES} notes; the excess was not imported.`);
  }

  return {
    format,
    ppq,
    tempoEvents: tempoEvents.sort((a, b) => a.tick - b.tick),
    timeSignatures: timeSignatures.sort((a, b) => a.tick - b.tick),
    tracks,
    warnings,
  };
}

interface OpenNote {
  startTicks: number;
  velocity: number;
}

function readTrack(
  reader: Reader,
  end: number,
  tempoEvents: MidiTempoEvent[],
  timeSignatures: MidiTimeSignatureEvent[],
  warnings: string[],
  atNoteLimit: () => boolean,
): MidiFileTrack {
  const notes: MidiFileNote[] = [];
  const controllers: MidiControllerEvent[] = [];
  const pitchBends: MidiPitchBendEvent[] = [];
  const programChanges: MidiProgramChangeEvent[] = [];
  const open = new Map<number, OpenNote[]>();
  const channels = new Set<number>();
  let name: string | null = null;
  let tick = 0;
  let runningStatus = 0;

  while (reader.offset < end) {
    tick += reader.varint();
    let status = reader.u8();

    if ((status & 0x80) === 0) {
      // Running status: a data byte where a status byte was expected means
      // "same message type as last time". Files lean on this heavily, and a
      // reader that ignores it produces garbage rather than an error.
      if (runningStatus === 0) throw new MidiFileError('The MIDI file uses running status before any status byte.');
      reader.offset--;
      status = runningStatus;
    } else if (status < 0xf0) {
      runningStatus = status;
    }

    if (status === 0xff) {
      const type = reader.u8();
      const length = reader.varint();
      const start = reader.offset;

      if (type === 0x03 && name === null) {
        name = decodeText(reader.bytes.subarray(start, start + length)) || null;
      } else if (type === 0x51 && length === 3) {
        const microseconds = (reader.bytes[start] << 16) | (reader.bytes[start + 1] << 8) | reader.bytes[start + 2];
        if (microseconds > 0) tempoEvents.push({ tick, bpm: 60_000_000 / microseconds });
      } else if (type === 0x58 && length >= 2) {
        timeSignatures.push({
          tick,
          numerator: Math.max(1, reader.bytes[start]),
          // The denominator is stored as a power of two.
          denominator: Math.max(1, Math.pow(2, reader.bytes[start + 1])),
        });
      }

      reader.offset = start;
      reader.skip(length);
      if (type === 0x2f) break;
      continue;
    }

    if (status === 0xf0 || status === 0xf7) {
      // System exclusive: nothing here can use it, and it is skipped by length.
      reader.skip(reader.varint());
      continue;
    }

    const command = status & 0xf0;
    const channel = status & 0x0f;

    if (command === 0x90 || command === 0x80) {
      const pitch = reader.u8() & 0x7f;
      const velocity = reader.u8() & 0x7f;
      const key = (channel << 8) | pitch;
      channels.add(channel);

      if (command === 0x90 && velocity > 0) {
        if (atNoteLimit()) continue;
        const stack = open.get(key) ?? [];
        stack.push({ startTicks: tick, velocity });
        open.set(key, stack);
        continue;
      }

      // Note off, including the note-on-with-zero-velocity spelling. The most
      // recent matching note is closed, so repeated notes at one pitch nest
      // correctly rather than closing the wrong one.
      const stack = open.get(key);
      const started = stack?.pop();
      if (!started) continue;
      notes.push({
        pitch,
        velocity: started.velocity,
        startTicks: started.startTicks,
        durationTicks: Math.max(1, tick - started.startTicks),
        channel,
      });
      continue;
    }

    if (command === 0xb0) {
      const controller = reader.u8() & 0x7f;
      const value = reader.u8() & 0x7f;
      channels.add(channel);
      if (controllers.length < MAX_EVENTS) controllers.push({ tick, channel, controller, value });
      continue;
    }

    if (command === 0xe0) {
      // Fourteen bits, low byte first, 8192 at rest. Divided by 8192 either way
      // rather than by 8191 upward: the resting point is what has to be exact,
      // and a bend that reads 0.99988 at the top is inaudibly short of full.
      const low = reader.u8() & 0x7f;
      const high = reader.u8() & 0x7f;
      channels.add(channel);
      const raw = (high << 7) | low;
      if (pitchBends.length < MAX_EVENTS) {
        pitchBends.push({ tick, channel, value: Math.max(-1, (raw - 8192) / 8192) });
      }
      continue;
    }

    if (command === 0xc0) {
      const program = reader.u8() & 0x7f;
      channels.add(channel);
      if (programChanges.length < MAX_EVENTS) programChanges.push({ tick, channel, program });
      continue;
    }

    // Everything else is a fixed-size channel message. Polyphonic aftertouch
    // and channel pressure are read past: nothing here could act on them, and
    // unlike a controller there is no lane they could ever land on.
    if (command === 0xa0) {
      reader.skip(2);
    } else if (command === 0xd0) {
      reader.skip(1);
    } else {
      throw new MidiFileError(`The MIDI file contains an unrecognised event (status 0x${status.toString(16)}).`);
    }
  }

  let dangling = 0;
  for (const stack of open.values()) dangling += stack.length;
  if (dangling > 0) {
    warnings.push(`${dangling} note(s) in "${name ?? 'a track'}" were never released and have been dropped.`);
  }

  notes.sort((a, b) => a.startTicks - b.startTicks || a.pitch - b.pitch);
  return {
    name,
    channel: channels.size === 1 ? [...channels][0] : null,
    notes,
    controllers,
    pitchBends,
    programChanges,
  };
}

// ---------------------------------------------------------------------------
// Transforming
// ---------------------------------------------------------------------------

/**
 * Rescales a parsed file to a different tick resolution.
 *
 * Files in the wild use 96, 120, 384, 480 and 960 ticks per quarter note among
 * others, and the project has one fixed resolution — so every imported file is
 * rescaled exactly once, here, rather than every reader having to remember.
 */
export function rescaleMidiFile(parsed: ParsedMidiFile, targetPpq: number): ParsedMidiFile {
  if (targetPpq === parsed.ppq) return parsed;
  const scale = targetPpq / parsed.ppq;
  const at = (tick: number): number => Math.round(tick * scale);

  return {
    ...parsed,
    ppq: targetPpq,
    tempoEvents: parsed.tempoEvents.map((e) => ({ ...e, tick: at(e.tick) })),
    timeSignatures: parsed.timeSignatures.map((e) => ({ ...e, tick: at(e.tick) })),
    tracks: parsed.tracks.map((track) => ({
      ...track,
      notes: track.notes.map((note) => ({
        ...note,
        startTicks: at(note.startTicks),
        // A note must not round away to nothing when scaling down.
        durationTicks: Math.max(1, at(note.durationTicks)),
      })),
      // A controller ride is positioned in the same ticks the notes are, so it
      // has to be rescaled with them or it would drift against the part it was
      // written for.
      controllers: track.controllers.map((e) => ({ ...e, tick: at(e.tick) })),
      pitchBends: track.pitchBends.map((e) => ({ ...e, tick: at(e.tick) })),
      programChanges: track.programChanges.map((e) => ({ ...e, tick: at(e.tick) })),
    })),
  };
}

/**
 * Splits a track whose notes span several channels into one track per channel.
 *
 * Format 0 files put the whole arrangement in a single track, distinguished
 * only by channel. Importing that as one part would pile a drum kit, a bass and
 * a piano onto one instrument.
 */
export function splitTracksByChannel(parsed: ParsedMidiFile): ParsedMidiFile {
  const tracks: MidiFileTrack[] = [];

  for (const track of parsed.tracks) {
    const byChannel = new Map<number, MidiFileNote[]>();
    for (const note of track.notes) {
      const list = byChannel.get(note.channel) ?? [];
      list.push(note);
      byChannel.set(note.channel, list);
    }

    if (byChannel.size <= 1) {
      tracks.push(track);
      continue;
    }
    for (const [channel, notes] of [...byChannel.entries()].sort((a, b) => a[0] - b[0])) {
      tracks.push({
        name: track.name ? `${track.name} · ch ${channel + 1}` : `Channel ${channel + 1}`,
        channel,
        notes,
        // The performance follows its channel, like the notes do. A volume ride
        // written for the bass belongs to the bass, and leaving all of them on
        // the first part would be worse than losing them.
        controllers: track.controllers.filter((e) => e.channel === channel),
        pitchBends: track.pitchBends.filter((e) => e.channel === channel),
        programChanges: track.programChanges.filter((e) => e.channel === channel),
      });
    }
  }

  return { ...parsed, tracks };
}

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

/**
 * What the writer needs of a track, which is less than a reader produces.
 *
 * Program changes are read but never written: this project has no way to
 * author one, so writing it would mean inventing an event the user never
 * made. Controllers and bends were in the same position until the wheels
 * could be recorded and drawn; a track may carry them now, and a track that
 * has none leaves them out.
 */
export type MidiWritableTrack = Pick<MidiFileTrack, 'name' | 'channel' | 'notes'> &
  Partial<Pick<MidiFileTrack, 'controllers' | 'pitchBends'>>;

export interface MidiFileInput {
  ppq: number;
  name?: string | null;
  tempoEvents: readonly MidiTempoEvent[];
  timeSignatures: readonly MidiTimeSignatureEvent[];
  tracks: readonly MidiWritableTrack[];
}

class Writer {
  private readonly bytes: number[] = [];

  u8(value: number): void {
    this.bytes.push(value & 0xff);
  }

  u16(value: number): void {
    this.u8(value >> 8);
    this.u8(value);
  }

  u32(value: number): void {
    this.u8(value >> 24);
    this.u8(value >> 16);
    this.u8(value >> 8);
    this.u8(value);
  }

  ascii(text: string): void {
    for (let i = 0; i < text.length; i++) this.u8(text.charCodeAt(i));
  }

  varint(value: number): void {
    const v = Math.max(0, Math.round(value));
    const chunks = [v & 0x7f];
    let rest = v >>> 7;
    while (rest > 0) {
      chunks.push((rest & 0x7f) | 0x80);
      rest >>>= 7;
    }
    for (let i = chunks.length - 1; i >= 0; i--) this.u8(chunks[i]);
  }

  raw(values: readonly number[]): void {
    for (const value of values) this.u8(value);
  }

  toArray(): number[] {
    return this.bytes;
  }
}

function chunk(id: string, body: number[]): number[] {
  const writer = new Writer();
  writer.ascii(id);
  writer.u32(body.length);
  return [...writer.toArray(), ...body];
}

interface TimedEvent {
  tick: number;
  /**
   * Note-offs sort before note-ons at the same tick so a repeated note
   * retriggers; a wheel sorts between them, so a note that starts on a bend
   * starts bent.
   */
  order: number;
  bytes: number[];
}

export function encodeMidiFile(input: MidiFileInput): Uint8Array {
  const ppq = Math.max(1, Math.round(input.ppq));
  const chunks: number[][] = [];

  // Track 0 carries the tempo map and meter, as format 1 requires.
  const conductor = new Writer();
  let last = 0;
  if (input.name) {
    conductor.varint(0);
    conductor.raw([0xff, 0x03]);
    const text = [...input.name].slice(0, 120).map((c) => c.charCodeAt(0) & 0x7f);
    conductor.varint(text.length);
    conductor.raw(text);
  }

  const conductorEvents: TimedEvent[] = [
    ...input.tempoEvents.map((event) => {
      const microseconds = Math.max(1, Math.round(60_000_000 / Math.max(1, event.bpm)));
      return {
        tick: Math.max(0, Math.round(event.tick)),
        order: 0,
        bytes: [0xff, 0x51, 0x03, (microseconds >> 16) & 0xff, (microseconds >> 8) & 0xff, microseconds & 0xff],
      };
    }),
    ...input.timeSignatures.map((event) => ({
      tick: Math.max(0, Math.round(event.tick)),
      order: 1,
      bytes: [
        0xff,
        0x58,
        0x04,
        Math.max(1, Math.round(event.numerator)),
        Math.round(Math.log2(Math.max(1, event.denominator))),
        24,
        8,
      ],
    })),
  ].sort((a, b) => a.tick - b.tick || a.order - b.order);

  for (const event of conductorEvents) {
    conductor.varint(event.tick - last);
    conductor.raw(event.bytes);
    last = event.tick;
  }
  conductor.varint(0);
  conductor.raw([0xff, 0x2f, 0x00]);
  chunks.push(chunk('MTrk', conductor.toArray()));

  for (const track of input.tracks) {
    const writer = new Writer();
    if (track.name) {
      writer.varint(0);
      writer.raw([0xff, 0x03]);
      const text = [...track.name].slice(0, 120).map((c) => c.charCodeAt(0) & 0x7f);
      writer.varint(text.length);
      writer.raw(text);
    }

    const channel = Math.max(0, Math.min(15, track.channel ?? 0));
    const events: TimedEvent[] = [];
    for (const note of track.notes) {
      const pitch = Math.max(0, Math.min(127, Math.round(note.pitch)));
      const velocity = Math.max(1, Math.min(127, Math.round(note.velocity)));
      const start = Math.max(0, Math.round(note.startTicks));
      const end = start + Math.max(1, Math.round(note.durationTicks));
      events.push({ tick: start, order: 2, bytes: [0x90 | channel, pitch, velocity] });
      events.push({ tick: end, order: 0, bytes: [0x80 | channel, pitch, 0] });
    }
    for (const event of track.controllers ?? []) {
      events.push({
        tick: Math.max(0, Math.round(event.tick)),
        order: 1,
        bytes: [0xb0 | channel, Math.max(0, Math.min(127, Math.round(event.controller))), Math.max(0, Math.min(127, Math.round(event.value)))],
      });
    }
    for (const event of track.pitchBends ?? []) {
      // Fourteen bits, least significant first, 8192 at rest — the reverse of
      // the reader, which scales by 8192 on both sides.
      const raw = Math.max(0, Math.min(16383, Math.round(8192 + Math.max(-1, Math.min(1, event.value)) * 8192)));
      events.push({ tick: Math.max(0, Math.round(event.tick)), order: 1, bytes: [0xe0 | channel, raw & 0x7f, raw >> 7] });
    }
    events.sort((a, b) => a.tick - b.tick || a.order - b.order);

    let previous = 0;
    for (const event of events) {
      writer.varint(event.tick - previous);
      writer.raw(event.bytes);
      previous = event.tick;
    }
    writer.varint(0);
    writer.raw([0xff, 0x2f, 0x00]);
    chunks.push(chunk('MTrk', writer.toArray()));
  }

  const header = new Writer();
  header.u16(1);
  header.u16(chunks.length);
  header.u16(ppq);

  const out = [...chunk('MThd', header.toArray()), ...chunks.flat()];
  return new Uint8Array(out);
}
